import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase";
import {
  disableWebChatPush,
  enableWebChatPush,
  syncExistingWebChatPush,
} from "./DriverManagerChatPush";

const text = (value) => String(value ?? "").trim();
const GROUP_CHAT_ID = (orgId) => `group_${text(orgId)}`;

function normalizeRole(value) {
  const role = text(value).toLowerCase();
  if (["owner", "primary", "primary_admin"].includes(role)) return "owner";
  if (["deputy", "deputy_admin"].includes(role)) return "deputy";
  return "driver";
}

function canCreateDirectChat(currentRole, targetRole) {
  if (currentRole === "owner") return ["deputy", "driver"].includes(targetRole);
  if (currentRole === "deputy") return targetRole === "driver";
  return false;
}

function normalizeMember(id, data = {}) {
  const uid = text(data.uid || id);
  return {
    uid,
    displayName:
      text(data.displayName || data.name || data.driverName) ||
      "未命名成員",
    photoURL: text(data.photo?.previewURL || data.photoURL || data.photoUrl || data.avatarUrl),
    role: normalizeRole(data.role || data.memberRole || data.orgRole),
    status: text(data.status || data.memberStatus || "active").toLowerCase(),
    phone: text(data.phone || data.phoneNumber),
  };
}

function directChatId(firstUid, secondUid) {
  return `direct_${[text(firstUid), text(secondUid)].sort().join("_")}`;
}

function roleLabel(role) {
  return { owner: "主管理員", deputy: "副管理員", driver: "成員" }[normalizeRole(role)] || "成員";
}

function getInitial(name) {
  return text(name).slice(0, 1).toUpperCase() || "聊";
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function formatChatTime(value) {
  if (!value) return "";
  const date = value?.toDate?.() || new Date(value || 0);
  if (!date || Number.isNaN(date.getTime())) return "";
  const pad = (number) => String(number).padStart(2, "0");
  return `${date.getFullYear()}年${pad(date.getMonth() + 1)}月${pad(date.getDate())}日 ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function normalizeInboxEntry(chatId, value = {}, bucketId = "") {
  const lastMessage = value.lastMessage || {};
  return {
    id: chatId,
    bucketId,
    chatType: text(value.chatType),
    title: text(value.title) || "聊天室",
    photoURL: text(value.groupPhotoPreviewURL || value.groupPhoto?.previewURL || value.photoURL || value.photoUrl),
    participantUids: Array.isArray(value.participantUids) ? value.participantUids.map(text).filter(Boolean) : [],
    participantMeta: value.participantMeta || {},
    lastMessageText:
      text(value.lastMessageText || lastMessage.text) || "尚無訊息",
    lastMessageAt: value.lastMessageAt || lastMessage.createdAt || null,
    lastMessageSenderUid: text(
      value.lastMessageSenderUid || lastMessage.senderUid,
    ),
    unreadCount: Math.max(0, Number(value.unreadCount || 0)),
    inboxBucketId: text(value.inboxBucketId || bucketId),
  };
}

function Icon({ name, size = 18 }) {
  const paths = {
    chat: "M5 5.5h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-7l-4.5 3v-3H5a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2Z",
    people: "M16 20a4 4 0 0 0-8 0M12 13a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm6.5 6a3.5 3.5 0 0 0-2.2-3.25M16.5 6.2a3 3 0 0 1 0 5.6",
    refresh: "M20 11a8 8 0 1 0 1 5M20 5v6h-6",
    search: "m21 21-4.35-4.35M19 11a8 8 0 1 1-16 0 8 8 0 0 1 16 0Z",
    arrow: "m9 18 6-6-6-6",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={paths[name] || paths.chat} />
    </svg>
  );
}

function Avatar({ name, photoURL, group = false }) {
  if (photoURL) return <img className={`dmcl-avatar${group ? " is-group" : ""}`} src={photoURL} alt="" loading="lazy" decoding="async" />;
  return <span className={`dmcl-avatar${group ? " is-group" : ""}`}>{group ? <Icon name="people" size={21} /> : getInitial(name)}</span>;
}

async function readActiveMembers(orgId) {
  const snapshot = await getDocs(query(collection(db, "drivers"), where("orgId", "==", orgId)));
  return snapshot.docs
    .map((item) => normalizeMember(item.id, item.data() || {}))
    .filter((member) => member.status === "active");
}

function normalizeInboxSnapshot(snapshot) {
  const entries = [];
  snapshot.docs.forEach((bucket) => {
    const data = bucket.data() || {};
    Object.entries(data.entries || {}).forEach(([chatId, entry]) => {
      entries.push(normalizeInboxEntry(chatId, entry, bucket.id));
    });
  });
  const byId = new Map();
  entries.forEach((entry) => byId.set(entry.id, entry));
  return Array.from(byId.values()).sort((left, right) => toMillis(right.lastMessageAt) - toMillis(left.lastMessageAt));
}

function chatMetaForMembers(members) {
  return Object.fromEntries(members.map((member) => [member.uid, {
    uid: member.uid,
    displayName: member.displayName,
    photoURL: member.photoURL,
    role: member.role,
  }]));
}

async function addInboxEntry(orgId, uid, chatId, entry) {
  const snapshot = await getDocs(collection(db, "orgs", orgId, "chatInboxes", uid, "buckets"));
  const buckets = snapshot.docs.map((item) => ({ id: item.id, data: item.data() || {} }));
  const existing = buckets.find((bucket) => bucket.data.entries?.[chatId]);
  const available = buckets.find((bucket) => Object.keys(bucket.data.entries || {}).length < 10);
  const nextNumber = buckets.reduce((max, bucket) => Math.max(max, Number(bucket.id.replace("bucket_", "")) || 0), 0) + 1;
  const bucketId = existing?.id || available?.id || `bucket_${String(nextNumber).padStart(4, "0")}`;
  const bucketRef = doc(db, "orgs", orgId, "chatInboxes", uid, "buckets", bucketId);
  const nextEntry = { ...entry, inboxBucketId: bucketId };
  await setDoc(bucketRef, {
    orgId,
    uid,
    bucketId,
    entries: { [chatId]: nextEntry },
    updatedAt: serverTimestamp(),
  }, { merge: true });
  return bucketId;
}

export default function DriverManagerChatList({ user: propUser, account, orgId: propOrgId = "", onOpenChat }) {
  const [authUser, setAuthUser] = useState(propUser || auth.currentUser || null);
  const [driverOrgId, setDriverOrgId] = useState("");
  const [driverOrgResolved, setDriverOrgResolved] = useState(false);
  const [members, setMembers] = useState([]);
  const [currentMember, setCurrentMember] = useState(null);
  const [inboxEntries, setInboxEntries] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushNotice, setPushNotice] = useState("");

  const user = propUser || authUser;
  const uid = text(user?.uid);
  const directOrgId = text(propOrgId || account?.orgId);
  const orgId = directOrgId || driverOrgId;
  const canCreateGroup = currentMember?.role === "owner" && currentMember?.status === "active";
  const orgName = text(account?.orgName || account?.companyName) || "車隊";

  useEffect(() => {
    if (propUser) {
      setAuthUser(propUser);
      return undefined;
    }
    return onAuthStateChanged(auth, (nextUser) => setAuthUser(nextUser || null));
  }, [propUser]);

  useEffect(() => {
    let alive = true;
    if (!uid) {
      setPushEnabled(false);
      return () => { alive = false; };
    }

    syncExistingWebChatPush(uid, window.location.pathname)
      .then((enabled) => { if (alive) setPushEnabled(enabled); })
      .catch((syncError) => {
        console.warn("[DriverManagerChatList] Web push sync failed:", syncError);
        if (alive) setPushEnabled(false);
      });

    return () => { alive = false; };
  }, [uid]);

  const handleToggleWebPush = useCallback(async () => {
    if (!uid || pushBusy) return;
    setPushBusy(true);
    setPushNotice("");
    try {
      if (pushEnabled) {
        await disableWebChatPush(uid);
        setPushEnabled(false);
        setPushNotice("已關閉此瀏覽器的聊天室通知。");
      } else {
        await enableWebChatPush(uid, window.location.pathname);
        setPushEnabled(true);
        setPushNotice("已開啟此瀏覽器的聊天室通知。");
      }
    } catch (pushError) {
      console.error("[DriverManagerChatList] Web push setup failed:", pushError);
      setPushNotice(pushError?.message || "通知設定失敗，請稍後再試。");
    } finally {
      setPushBusy(false);
    }
  }, [pushBusy, pushEnabled, uid]);

  useEffect(() => {
    let alive = true;
    setDriverOrgId("");
    if (directOrgId || !uid) {
      setDriverOrgResolved(true);
      return undefined;
    }

    setDriverOrgResolved(false);
    getDoc(doc(db, "drivers", uid))
      .then((snapshot) => {
        if (!alive) return;
        setDriverOrgId(snapshot.exists() ? text(snapshot.data()?.orgId) : "");
        setDriverOrgResolved(true);
      })
      .catch((profileError) => {
        console.error("[DriverManagerChatList] driver profile error:", profileError);
        if (!alive) return;
        setDriverOrgId("");
        setDriverOrgResolved(true);
      });

    return () => {
      alive = false;
    };
  }, [directOrgId, uid]);

  useEffect(() => {
    let alive = true;
    let unsubscribe = () => {};

    setLoading(true);
    setError("");
    setCurrentMember(null);
    setMembers([]);
    setInboxEntries([]);

    if (!uid) {
      setLoading(false);
      setError("尚未取得登入狀態。");
      return () => { alive = false; };
    }
    if (!driverOrgResolved) return () => { alive = false; };
    if (!orgId) {
      setLoading(false);
      setError("目前登入帳號尚未取得 drivers/{uid}.orgId。");
      return () => { alive = false; };
    }

    readActiveMembers(orgId)
      .then((activeMembers) => {
        if (!alive) return;
        const me = activeMembers.find((member) => member.uid === uid) || null;
        setCurrentMember(me);
        setMembers(activeMembers);
        if (!me || me.status !== "active") setError("目前帳號不是這個組織的有效成員。");

        unsubscribe = onSnapshot(
          collection(db, "orgs", orgId, "chatInboxes", uid, "buckets"),
          (snapshot) => {
            if (!alive) return;
            setInboxEntries(normalizeInboxSnapshot(snapshot));
            setLoading(false);
          },
          (inboxError) => {
            console.error("[DriverManagerChatList] inbox summary error:", inboxError);
            if (!alive) return;
            setInboxEntries([]);
            setLoading(false);
            setError("聊天室摘要讀取失敗，請確認組織權限後再試。");
          },
        );
      })
      .catch((loadError) => {
        console.error("[DriverManagerChatList] load error:", loadError);
        if (!alive) return;
        setMembers([]);
        setInboxEntries([]);
        setLoading(false);
        setError("聊天室摘要讀取失敗，請確認組織權限後再試。");
      });

    return () => {
      alive = false;
      unsubscribe();
    };
  }, [driverOrgResolved, orgId, refreshKey, uid]);

  const entriesById = useMemo(() => new Map(inboxEntries.map((entry) => [entry.id, entry])), [inboxEntries]);
  const groupId = GROUP_CHAT_ID(orgId);
  const groupEntry = entriesById.get(groupId) || null;
  const chatMembers = useMemo(() => {
    const search = text(keyword).toLowerCase();
    return members
      .filter((member) => member.uid !== uid && (!search || member.displayName.toLowerCase().includes(search)))
      .sort((left, right) => {
        const leftEntry = entriesById.get(directChatId(uid, left.uid));
        const rightEntry = entriesById.get(directChatId(uid, right.uid));
        if (Boolean(leftEntry) !== Boolean(rightEntry)) return rightEntry ? 1 : -1;
        return toMillis(rightEntry?.lastMessageAt) - toMillis(leftEntry?.lastMessageAt) || left.displayName.localeCompare(right.displayName, "zh-Hant");
      });
  }, [entriesById, keyword, members, uid]);

  const openChat = useCallback(async (chat, fallbackTitle = "聊天室", onMissing) => {
    if (!chat?.id || !onOpenChat) return;
    let chatSnapshot;
    try {
      chatSnapshot = await getDoc(doc(db, "orgs", orgId, "chats", chat.id));
    } catch (chatError) {
      console.error("[DriverManagerChatList] chat lookup error:", chatError);
      setError("聊天室目前無法讀取，請稍後再試。");
      return;
    }
    if (!chatSnapshot.exists()) {
      const bucketId = text(chat.inboxBucketId || chat.bucketId);
      if (bucketId) {
        try {
          await updateDoc(
            doc(db, "orgs", orgId, "chatInboxes", uid, "buckets", bucketId),
            {
              [`entries.${chat.id}`]: deleteField(),
              updatedAt: serverTimestamp(),
            },
          );
        } catch (removeError) {
          console.error("[DriverManagerChatList] stale chat cleanup error:", removeError);
        }
      }
      setInboxEntries((previous) => previous.filter((entry) => entry.id !== chat.id));
      if (onMissing) return onMissing();
      setError("這個聊天室已被刪除，請重新建立聊天室。");
      return;
    }
    onOpenChat({ orgId, chatId: chat.id, chatType: chat.chatType, title: chat.title || fallbackTitle, inboxBucketId: chat.inboxBucketId || chat.bucketId || "bucket_0001" });
  }, [onOpenChat, orgId, uid]);

  const createDirectChat = useCallback(async (member, { force = false } = {}) => {
    if (!canCreateDirectChat(currentMember?.role, member?.role) || !member?.uid || member.uid === uid || member.status !== "active") return;
    const chatId = directChatId(uid, member.uid);
    const existing = entriesById.get(chatId);
    if (existing && !force) {
      return openChat(
        existing,
        member.displayName,
        () => createDirectChat(member, { force: true }),
      );
    }
    setBusyId(chatId);
    try {
      const participantUids = [uid, member.uid].sort();
      const participantMeta = chatMetaForMembers([currentMember, member].filter(Boolean));
      const chat = {
        orgId,
        chatType: "direct",
        title: member.displayName,
        participantUids,
        participantMeta,
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessageText: "",
        lastMessageAt: null,
        lastMessageSenderUid: "",
        lastMessage: null,
        activeChunkId: "",
        recentChunkIds: [],
        activeMessageCount: 0,
        nextChunkIndex: 1,
      };
      const chatRef = doc(db, "orgs", orgId, "chats", chatId);
      const chatSnapshot = await getDoc(chatRef);
      const existingChat = chatSnapshot.exists() ? { id: chatSnapshot.id, ...(chatSnapshot.data() || {}) } : null;
      if (existingChat && (!Array.isArray(existingChat.participantUids) || !participantUids.every((participantUid) => existingChat.participantUids.includes(participantUid)))) {
        throw new Error("direct_chat_participant_conflict");
      }
      const sourceChat = existingChat || chat;
      if (!existingChat) await setDoc(chatRef, chat);
      const entry = { chatType: sourceChat.chatType || "direct", title: sourceChat.title || member.displayName, participantUids, participantMeta: sourceChat.participantMeta || participantMeta, lastMessageText: sourceChat.lastMessageText || "尚無訊息", lastMessageAt: sourceChat.lastMessageAt || sourceChat.lastMessage?.createdAt || null, lastMessageSenderUid: sourceChat.lastMessageSenderUid || sourceChat.lastMessage?.senderUid || "", unreadCount: 0 };
      const ownBucketId = await addInboxEntry(orgId, uid, chatId, entry);
      const otherBucketId = await addInboxEntry(orgId, member.uid, chatId, { ...entry, unreadCount: 0 });
      await setDoc(chatRef, { inboxBucketByUid: { ...(sourceChat.inboxBucketByUid || {}), [uid]: ownBucketId, [member.uid]: otherBucketId }, updatedAt: serverTimestamp() }, { merge: true });
      const created = { id: chatId, ...entry, bucketId: ownBucketId, inboxBucketId: ownBucketId };
      setInboxEntries((previous) => [...previous, created]);
      openChat(created, member.displayName);
    } catch (createError) {
      console.error("[DriverManagerChatList] direct chat error:", createError);
      setError("一對一聊天室建立失敗，請稍後再試。");
    } finally {
      setBusyId("");
    }
  }, [currentMember, entriesById, openChat, orgId, uid]);

  const createGroupChat = useCallback(async ({ force = false } = {}) => {
    if (!canCreateGroup) return;
    const chatId = GROUP_CHAT_ID(orgId);
    if (groupEntry && !force) {
      return openChat(
        groupEntry,
        `${orgName} 車隊群組`,
        () => createGroupChat({ force: true }),
      );
    }
    setBusyId(chatId);
    try {
      const draftParticipantUids = members.map((member) => member.uid).filter(Boolean).sort();
      const draftParticipantMeta = chatMetaForMembers(members);
      const draftChat = {
        orgId,
        chatType: "group",
        title: `${orgName} 車隊群組`,
        participantUids: draftParticipantUids,
        participantMeta: draftParticipantMeta,
        createdBy: uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastMessageText: "",
        lastMessageAt: null,
        lastMessageSenderUid: "",
        lastMessage: null,
        activeChunkId: "",
        recentChunkIds: [],
        activeMessageCount: 0,
        nextChunkIndex: 1,
      };
      const chatRef = doc(db, "orgs", orgId, "chats", chatId);
      const chatSnapshot = await getDoc(chatRef);
      const existingChat = chatSnapshot.exists() ? { id: chatSnapshot.id, ...(chatSnapshot.data() || {}) } : null;
      const sourceChat = existingChat || draftChat;
      const participantUids = Array.isArray(sourceChat.participantUids) && sourceChat.participantUids.length ? sourceChat.participantUids : draftParticipantUids;
      const participantMeta = sourceChat.participantMeta || draftParticipantMeta;
      if (!existingChat) await setDoc(chatRef, draftChat);
      const entry = { chatType: sourceChat.chatType || "group", title: sourceChat.title || draftChat.title, participantUids, participantMeta, lastMessageText: sourceChat.lastMessageText || "尚無訊息", lastMessageAt: sourceChat.lastMessageAt || sourceChat.lastMessage?.createdAt || null, lastMessageSenderUid: sourceChat.lastMessageSenderUid || sourceChat.lastMessage?.senderUid || "", unreadCount: 0 };
      const bucketPairs = await Promise.all(participantUids.map(async (participantUid) => [participantUid, await addInboxEntry(orgId, participantUid, chatId, entry)]));
      const inboxBucketByUid = { ...(sourceChat.inboxBucketByUid || {}), ...Object.fromEntries(bucketPairs) };
      await setDoc(chatRef, { inboxBucketByUid, updatedAt: serverTimestamp() }, { merge: true });
      const created = { id: chatId, ...entry, bucketId: inboxBucketByUid[uid], inboxBucketId: inboxBucketByUid[uid] };
      setInboxEntries((previous) => [...previous, created]);
      openChat(created, entry.title);
    } catch (createError) {
      console.error("[DriverManagerChatList] group chat error:", createError);
      setError("群組聊天室建立失敗，請稍後再試。");
    } finally {
      setBusyId("");
    }
  }, [canCreateGroup, groupEntry, members, openChat, orgId, orgName, uid]);

  const filteredInbox = useMemo(() => {
    const search = text(keyword).toLowerCase();
    return inboxEntries.filter((entry) => !search || entry.title.toLowerCase().includes(search));
  }, [inboxEntries, keyword]);

  if (loading) return <div className="dmcl-root"><style>{styles}</style><div className="dmcl-state"><span className="dmcl-spinner" /><strong>正在載入聊天室摘要</strong></div></div>;
  if (error && !currentMember) return <div className="dmcl-root"><style>{styles}</style><div className="dmcl-state is-error"><strong>{error}</strong><button type="button" onClick={() => setRefreshKey((value) => value + 1)}>重新讀取</button></div></div>;

  return (
    <div className="dmcl-root">
      <style>{styles}</style>
      <main className="dmcl-page">
        <header className="dmcl-header">
          <div><h1>聊天室</h1></div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              type="button"
              onClick={handleToggleWebPush}
              disabled={pushBusy || !uid}
              aria-pressed={pushEnabled}
              style={{ height: 36, padding: "0 12px", color: pushEnabled ? "#217447" : "#46535c", background: pushEnabled ? "#eef8f2" : "#fff", border: `1px solid ${pushEnabled ? "#c7e8d2" : "#e1e7eb"}`, borderRadius: 10, cursor: pushBusy || !uid ? "default" : "pointer", opacity: pushBusy || !uid ? 0.65 : 1, fontSize: 13, fontWeight: 750, whiteSpace: "nowrap" }}
            >
              {pushBusy ? "設定中…" : pushEnabled ? "關閉通知" : "開啟通知"}
            </button>
            <button type="button" className="dmcl-refresh" onClick={() => setRefreshKey((value) => value + 1)} disabled={Boolean(busyId)} aria-label="重新整理"><Icon name="refresh" size={17} /></button>
          </div>
        </header>
        {pushNotice && <div role="status" style={{ margin: "-10px 0 16px", padding: "9px 12px", color: pushEnabled ? "#286747" : "#5d6870", background: pushEnabled ? "#f1f9f4" : "#f6f8f9", border: `1px solid ${pushEnabled ? "#d4ebdc" : "#e7ecef"}`, borderRadius: 9, fontSize: 13 }}>{pushNotice}</div>}
        {error && <div className="dmcl-inline-error">{error}</div>}

        <section className="dmcl-group-section is-clickable" style={{ cursor: "pointer" }} onClick={() => (groupEntry ? openChat(groupEntry, groupEntry.title, () => createGroupChat({ force: true })) : canCreateGroup ? createGroupChat() : setError("目前尚未建立群組聊天室。"))} role="button" tabIndex={0} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); groupEntry ? openChat(groupEntry, groupEntry.title, () => createGroupChat({ force: true })) : canCreateGroup ? createGroupChat() : setError("目前尚未建立群組聊天室。"); } }}>
          <div className="dmcl-section-label">車隊群組</div>
          <div className="dmcl-group-row">
            <Avatar group name={groupEntry?.title || orgName} photoURL={groupEntry?.photoURL} />
            <div className="dmcl-group-main"><div className="dmcl-row-title-line"><strong>{groupEntry?.title || `${orgName} 車隊群組`}</strong></div><div className="dmcl-row-message-line"><span className={`dmcl-preview${groupEntry?.unreadCount > 0 ? " is-unread" : ""}`} style={groupEntry?.unreadCount > 0 ? { fontWeight: 800, color: "#33434d" } : undefined}>{groupEntry?.lastMessageText || (canCreateGroup ? "點擊建立群組聊天室" : "尚未建立群組聊天室")}</span>{groupEntry?.unreadCount > 0 && <span className="dmcl-unread" aria-label="有未讀訊息" />}</div><div className="dmcl-group-info"><span>{groupEntry?.participantUids?.length || members.length} 位成員</span>{canCreateGroup && !groupEntry && <span>主管理員可建立</span>}</div></div>
            {groupEntry && <span className="dmcl-time dmcl-time-column" style={{ flex: "0 0 142px", textAlign: "center", whiteSpace: "nowrap" }}>{formatChatTime(groupEntry.lastMessageAt)}</span>}
            {!groupEntry && canCreateGroup ? <button type="button" onClick={(event) => { event.stopPropagation(); createGroupChat(); }} style={{ minWidth: 142, height: 40, padding: "0 16px", color: "#fff", background: "linear-gradient(135deg,#ff9b3d,#f07818)", border: "0", borderRadius: 11, boxShadow: "0 7px 16px rgba(240,120,24,.22)", cursor: "pointer", fontSize: 14, fontWeight: 800, whiteSpace: "nowrap" }}>建立群組聊天室</button> : null}
          </div>
        </section>

        <section className="dmcl-members-section">
          <div className="dmcl-members-heading"><div className="dmcl-members-title"><h2>一對一聊天室</h2><span>{chatMembers.length}</span></div><div className="dmcl-search"><Icon name="search" size={17} /><input value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="搜尋成員" aria-label="搜尋成員" /></div></div>
          <div className="dmcl-list">
            {chatMembers.map((member) => {
              const chatId = directChatId(uid, member.uid);
              const entry = entriesById.get(chatId);
              const canCreateWithMember = canCreateDirectChat(currentMember?.role, member.role);
              const clickable = !busyId && (Boolean(entry) || canCreateWithMember);
              const handleMemberClick = () => {
                if (busyId) return;
                if (entry) {
                  return openChat(
                    entry,
                    member.displayName,
                    () => createDirectChat(member, { force: true }),
                  );
                }
                if (canCreateWithMember) createDirectChat(member);
                else setError("目前帳號只能查看既有聊天室，無法主動建立這個對話。");
              };
              return <div key={member.uid} className={`dmcl-member-row${clickable ? " is-clickable" : ""}`} onClick={clickable ? handleMemberClick : undefined} role={clickable ? "button" : undefined} tabIndex={clickable ? 0 : undefined} onKeyDown={(event) => { if (clickable && (event.key === "Enter" || event.key === " ")) { event.preventDefault(); handleMemberClick(); } }}>
                <Avatar name={member.displayName} photoURL={member.photoURL} />
                <div className="dmcl-member-main"><div className="dmcl-row-title-line"><strong>{member.displayName}</strong></div><div className="dmcl-row-message-line"><span className={`dmcl-preview${entry?.unreadCount > 0 ? " is-unread" : ""}`} style={entry?.unreadCount > 0 ? { fontWeight: 800, color: "#33434d" } : undefined}>{entry?.lastMessageText || (canCreateWithMember ? "點擊建立對話" : "尚未建立一對一聊天室")}</span>{entry?.unreadCount > 0 && <span className="dmcl-unread" aria-label="有未讀訊息" />}</div><div className="dmcl-member-meta"><span className="dmcl-role-badge">{roleLabel(member.role)}</span>{entry && <span className="dmcl-open-hint">查看對話</span>}</div></div>
                {entry && <span className="dmcl-time dmcl-time-column" style={{ flex: "0 0 142px", textAlign: "center", whiteSpace: "nowrap" }}>{formatChatTime(entry.lastMessageAt)}</span>}
                {!entry && canCreateWithMember && <button type="button" onClick={(event) => { event.stopPropagation(); createDirectChat(member); }} style={{ minWidth: 118, height: 38, padding: "0 15px", color: "#fff", background: "linear-gradient(135deg,#ff9b3d,#f07818)", border: "0", borderRadius: 10, boxShadow: "0 6px 14px rgba(240,120,24,.2)", cursor: "pointer", fontSize: 14, fontWeight: 800, whiteSpace: "nowrap" }}>建立聊天室</button>}
              </div>;
            })}
            {!chatMembers.length && <div className="dmcl-empty"><Icon name="people" size={22} /><strong>{filteredInbox.length ? "請用上方搜尋聊天室" : "目前沒有可建立的一對一對話"}</strong></div>}
          </div>
        </section>
      </main>
    </div>
  );
}

const styles = `
.dmcl-root.dmcl-root{width:auto;height:100vh;height:100dvh;min-height:0;margin:-22px;padding:0;overflow:hidden;display:flex;flex-direction:column;box-sizing:border-box;background:#fff}
.dmcl-root .dmcl-page{width:100%;height:100%;min-height:0;flex:1 1 auto;padding:22px;overflow-y:auto;overflow-x:hidden;border:1px solid rgba(148,163,184,.12);border-radius:24px;background:#fff;box-shadow:0 12px 28px rgba(15,23,42,.05)}
@media(max-width:760px){.dmcl-root .dmcl-page{padding:18px}}
@media(max-width:480px){.dmcl-root .dmcl-page{padding:14px}}
.dmcl-root{width:100%;height:100%;min-height:100%;color:#17212b;background:transparent;font-family:Inter,"Noto Sans TC",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.dmcl-root *{box-sizing:border-box}.dmcl-root button,.dmcl-root input{font:inherit}.dmcl-page{width:100%;height:calc(100vh - 90px);min-height:0;display:flex;flex-direction:column;padding:0 0 20px;overflow-y:scroll;overflow-x:hidden;scrollbar-width:thin;scrollbar-color:#aeb9c1 transparent}.dmcl-page::-webkit-scrollbar{width:8px}.dmcl-page::-webkit-scrollbar-track{background:transparent}.dmcl-page::-webkit-scrollbar-thumb{background:#b5c0c8;border-radius:999px}.dmcl-page::-webkit-scrollbar-thumb:hover{background:#8f9da7}.dmcl-header{min-height:54px;display:flex;align-items:flex-start;justify-content:space-between;gap:20px;margin-bottom:20px;padding-bottom:15px;border-bottom:1px solid #f0f3f5}.dmcl-header h1{margin:0;color:#17212b;font-size:28px;line-height:1.2;font-weight:850;letter-spacing:-.045em}.dmcl-header p{margin:6px 0 0;color:#87949d;font-size:14px}.dmcl-refresh{width:36px;height:36px;display:grid;place-items:center;flex:0 0 36px;padding:0;color:#737d85;background:#fff;border:1px solid #e6eaed;border-radius:10px;cursor:pointer}.dmcl-refresh:disabled{opacity:.42;cursor:default}.dmcl-inline-error{margin:-10px 0 20px;padding:10px 12px;color:#a44942;font-size:14px;background:#fff6f4;border:1px solid #f2d8d4;border-radius:9px}.dmcl-section-label{display:flex;align-items:center;gap:8px;margin-bottom:11px;color:#60727e;font-size:14px;font-weight:850}.dmcl-group-section{width:100%;padding:16px;border:1px solid #e5edf2;border-radius:18px;background:linear-gradient(135deg,#fff 0%,#f8fbfd 100%);box-shadow:0 8px 22px rgba(36,67,86,.05)}.dmcl-group-row,.dmcl-member-row{position:relative;width:100%;display:flex;align-items:center;gap:13px;padding:14px;border:1px solid #e4edf2;border-radius:14px;background:#fff;outline:none;transition:background .16s,border-color .16s,box-shadow .16s,transform .16s}.dmcl-group-row{min-height:88px}.dmcl-member-row{min-height:72px;gap:12px}.dmcl-group-row.is-clickable,.dmcl-member-row.is-clickable{cursor:pointer}.dmcl-group-row.is-clickable:hover,.dmcl-member-row.is-clickable:hover{background:#fbfdff;border-color:#b9d7e8;box-shadow:0 8px 18px rgba(36,123,209,.08);transform:translateY(-1px)}.dmcl-avatar{width:44px;height:44px;display:grid;place-items:center;flex:0 0 44px;border-radius:13px;color:#376f9f;background:#eaf2f8;font-size:15px;font-weight:760;object-fit:cover}.dmcl-avatar.is-group{color:#56626d;background:#e9ecef}.dmcl-group-main,.dmcl-member-main{min-width:0;flex:1}.dmcl-row-title-line,.dmcl-row-message-line{min-width:0;display:flex;align-items:center;justify-content:space-between;gap:14px}.dmcl-row-message-line{justify-content:flex-start;gap:10px;margin-top:5px}.dmcl-row-title-line strong{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#20262b;font-size:16px;font-weight:680}.dmcl-time{flex:0 0 auto;color:#9aa2a9;font-size:13px;font-weight:800}.dmcl-preview{min-width:0;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#77828a;font-size:14px}.dmcl-group-info,.dmcl-member-meta{display:flex;align-items:center;gap:7px;margin-top:7px;color:#8a9aa4;font-size:13px}.dmcl-role-badge{padding:3px 6px;border-radius:999px;background:#eef5f9;color:#628093;font-size:11px;font-weight:800}.dmcl-open-hint{color:#9aaab3;font-size:11px}.dmcl-row-arrow{display:grid;place-items:center;width:28px;height:28px;flex:0 0 28px;border:1px solid #e5edf2;border-radius:9px;color:#8ca0ab;background:#f8fbfd}.dmcl-unread{width:10px;height:10px;display:inline-block;flex:0 0 10px;padding:0;color:transparent;background:#e5484d;border-radius:50%;font-size:0;line-height:0}.dmcl-members-section{width:100%;min-height:0;margin-top:22px}.dmcl-members-heading{width:100%;display:flex;align-items:center;justify-content:space-between;gap:18px;margin-bottom:11px}.dmcl-members-title{display:flex;align-items:center;gap:7px}.dmcl-members-title h2{margin:0;color:#20262b;font-size:16px;font-weight:700}.dmcl-members-title>span{min-width:22px;height:20px;display:grid;place-items:center;padding:0 6px;color:#7d878f;background:#f0f2f4;border-radius:999px;font-size:13px}.dmcl-search{width:min(360px,100%);height:36px;display:flex;align-items:center;gap:8px;padding:0 11px;color:#9aa3aa;background:#f6f7f8;border:1px solid transparent;border-radius:9px}.dmcl-search:focus-within{background:#fff;border-color:#d6dde2}.dmcl-search input{min-width:0;width:100%;padding:0;color:#20262b;background:transparent;border:0;outline:none;font-size:14px}.dmcl-list{width:100%;display:grid;gap:8px;padding-bottom:20px}.dmcl-empty,.dmcl-state{min-height:180px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;color:#89939a;text-align:center}.dmcl-state{min-height:320px}.dmcl-state.is-error{color:#9b4c47}.dmcl-state button{height:34px;padding:0 13px;color:#47525a;background:#fff;border:1px solid #dde3e7;border-radius:8px;cursor:pointer}.dmcl-spinner{width:25px;height:25px;border:2.5px solid #e7ecef;border-top-color:#5e7e99;border-radius:50%;animation:dmcl-spin .7s linear infinite}@keyframes dmcl-spin{to{transform:rotate(360deg)}}@media(max-width:760px){.dmcl-header{margin-bottom:17px}.dmcl-header h1{font-size:23px}.dmcl-group-section{padding:12px}.dmcl-members-heading{align-items:stretch;flex-direction:column;gap:12px}.dmcl-search{width:100%}}@media(max-width:480px){.dmcl-avatar{width:42px;height:42px;flex-basis:42px}.dmcl-group-row,.dmcl-member-row{padding:12px}.dmcl-row-title-line strong{font-size:12.5px}}
`;
